import React from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { PromptSelect } from './PromptSelect'
import { ModelSelect } from './ModelSelect'
import { ProviderSelect } from './ProviderSelect'
import { usePromptRunner } from './hooks'
import type { PromptRunnerProps } from './types'

export function PromptRunner({ area }: PromptRunnerProps) {
  const {
    selectedPrompt,
    modelOverride,
    providerOverride,
    userPrompts,
    result,
    rawResultJson,
    error,
    running,
    setModelOverride,
    setProviderOverride,
    handleAddPrompt,
    handleRemovePrompt,
    handlePromptChange,
    handleRunPrompt,
  } = usePromptRunner(area)

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Choose Prompt
        </Typography>
        <Box sx={{ mt: 2 }}>
          <PromptSelect />
        </Box>
        <Box sx={{ mt: 2 }}>
          <ProviderSelect />
        </Box>
        <Box sx={{ mt: 2 }}>
          <ModelSelect />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Run Prompt
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <TextField
            label="Provider"
            value={providerOverride}
            onChange={(e) => setProviderOverride(e.target.value)}
            placeholder="e.g., openrouter, openai"
            variant="outlined"
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Model"
            value={modelOverride}
            onChange={(e) => setModelOverride(e.target.value)}
            placeholder="e.g., openai/gpt-4o-mini"
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            User Prompts
          </Typography>
          {userPrompts.map((prompt, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}
            >
              <TextField
                value={prompt}
                onChange={(e) => handlePromptChange(index, e.target.value)}
                placeholder={`User prompt ${index + 1}`}
                multiline
                rows={2}
                variant="outlined"
                size="small"
                fullWidth
              />
              <IconButton
                onClick={() => handleRemovePrompt(index)}
                color="error"
                size="small"
                disabled={userPrompts.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddPrompt}
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          >
            Add Prompt
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleRunPrompt}
            disabled={
              running ||
              !selectedPrompt ||
              !modelOverride.trim() ||
              !providerOverride.trim()
            }
            startIcon={running ? <CircularProgress size={16} /> : null}
          >
            {running ? 'Running...' : 'Run Prompt'}
          </Button>
        </Box>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {result && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Formatted Response
            </Typography>

            {/* System Prompt */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                System Prompt
              </Typography>
              <TextField
                value={result.data.system_prompt || ''}
                multiline
                rows={6}
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  },
                }}
              />
            </Paper>

            {/* User Prompts and Responses */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 2 }}
              >
                Prompts and Responses
              </Typography>
              {result.data.input_prompts?.map((inputPrompt, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                    >
                      User Prompt {index + 1}
                    </Typography>
                    <TextField
                      value={inputPrompt || ''}
                      multiline
                      rows={3}
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{ mt: 0.5 }}
                      slotProps={{
                        input: {
                          readOnly: true,
                          style: {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                    >
                      Response {index + 1}
                    </Typography>
                    <TextField
                      value={result.data.output_responses?.[index] || ''}
                      multiline
                      rows={4}
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{ mt: 0.5 }}
                      slotProps={{
                        input: {
                          readOnly: true,
                          style: {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Paper>

            {/* Telemetry */}
            {result.data.telemetry && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  Telemetry
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {result.data.telemetry.prompt_tokens !== undefined && (
                    <Typography variant="body2">
                      <strong>Prompt Tokens:</strong>{' '}
                      {result.data.telemetry.prompt_tokens}
                    </Typography>
                  )}
                  {result.data.telemetry.completion_tokens !== undefined && (
                    <Typography variant="body2">
                      <strong>Completion Tokens:</strong>{' '}
                      {result.data.telemetry.completion_tokens}
                    </Typography>
                  )}
                  {result.data.telemetry.total_tokens !== undefined && (
                    <Typography variant="body2">
                      <strong>Total Tokens:</strong>{' '}
                      {result.data.telemetry.total_tokens}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            {/* Raw Results */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Raw Results
              </Typography>
              <TextField
                value={rawResultJson}
                multiline
                rows={20}
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
